import { AuditDTO } from 'src/app/core/models/system/audit/audit.dto';
import { AuditService } from 'src/app/core/services/utelize/system/audit/audit-service';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { CompaniesService } from 'src/app/core/services/utelize/system/company/companies-service';
import { Component, EventEmitter, OnDestroy, Output, TemplateRef, ViewChild } from '@angular/core';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { IdAndDescriptionDTO } from 'src/app/core/interfaces/general/id-description';
import { msSize } from 'src/app/core/consts';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription } from 'rxjs';
import { SystemUserDTO } from 'src/app/core/models/system/user/system-user.dto';
import { UsersService } from 'src/app/core/services/utelize/system/user/users-service';

export enum BulkUserAction {
  TransferCompany = 1,
}

export interface BulkUserActionResult {
  companyName: string;
  message    : string;
  ok         : boolean;
  userId     : number;
  userName   : string;
}

export interface BulkUserRow {
  companyId  : number;
  companyName: string;
  userId     : number;
  userName   : string;
}

@Component({
  selector: 'app-bulk-user-actions-modal-form',
  templateUrl: './bulk-user-actions-modal-form.component.html',
  styleUrls: ['./bulk-user-actions-modal-form.component.scss']
})

export class BulkUserActionsModalFormComponent implements OnDestroy {

  @Output() completed: EventEmitter<void>=new EventEmitter<void>();

  @ViewChild('bulkUserActionsModalForm', { static: false }) bulkUserActionsModalForm!: TemplateRef<void>;

  private currentIndex : number=0;
  private modalRef     : NgbModalRef | undefined;
  private processingSub: Subscription | undefined;

  public action           : BulkUserAction=BulkUserAction.TransferCompany;
  public BulkUserAction    =BulkUserAction;
  public companies        : IdAndDescriptionDTO[]=[];
  public confirmSummary   : string="";
  public curUser          : SystemUserDTO;
  public errorCount       : number=0;
  public formSubmitted    : boolean=false;
  public processing       : boolean=false;
  public progressPercent  : number=0;
  public results          : BulkUserActionResult[]=[];
  public runRows          : BulkUserRow[]=[];
  public selectedCompanyId: number | null=null;
  public selectedRows     : BulkUserRow[]=[];
  public skippedRows      : BulkUserRow[]=[];
  public step             : string="configure";
  public successCount     : number=0;
  public totalCount       : number=0;

  get modalBackGround(): boolean | "static" { return "static"; }

  constructor(
    private auditService         : AuditService,
    private authenticationService: AuthenticationService,
    private companiesService     : CompaniesService,
    private exceptionsService    : ExceptionsService,
    private modalService         : NgbModal,
    private usersService         : UsersService
  ) {
    this.curUser=this.authenticationService.currentUserObject();
  }

  public ngOnDestroy(): void {
    try {
      this.processingSub?.unsubscribe();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#region Getters

  public get currentIndexDisplay(): number {
    try {
      return Math.min(this.currentIndex + 1, this.totalCount);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  public get actionLabel(): string {
    try {
      switch(this.action) {
        case BulkUserAction.TransferCompany: return "Transfer to Another Company";
        default                            : return "";
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  public get selectedCompanyName(): string {
    try {
      return this.companies.find(x => x.id===this.selectedCompanyId)?.description ?? "";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  //#endregion

  //#region Private Methods

  private auditBulk(message: string, additionalInfo: string): void {
    try {
      const audit: AuditDTO={
        additionalInfo: additionalInfo,
        fromFrontend  : true,
        isError       : false,
        message       : message,
        userID        : this.curUser.userId,
      }

      this.auditService.post(audit)
      .subscribe({
        next : ()    => { },
        error: error => { this.exceptionsService.handleException(new Error(error)); }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private userListOrNone(userIds: number[]): string {
    try {
      return (userIds != null && userIds.length>0) ? userIds.join(", ") : "None";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "None";
    }
  }

  private auditResultsInfo(): string {
    try {
      const completed: number[]=this.results.filter(x =>  x.ok).map(x => x.userId);
      const failed   : number[]=this.results.filter(x => !x.ok).map(x => x.userId);

      return "Completed: " + this.userListOrNone(completed) + "\nFailed: " + this.userListOrNone(failed);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  private buildRunRows(): void {
    try {
      this.runRows    =this.selectedRows.filter(x => x.companyId!==this.selectedCompanyId);
      this.skippedRows=this.selectedRows.filter(x => x.companyId===this.selectedCompanyId);
      this.totalCount =this.runRows.length;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private buildConfirmSummary(): void {
    try {
      switch(this.action) {
        case BulkUserAction.TransferCompany:
          this.confirmSummary="Transfer <strong>" + this.totalCount + "</strong> subscriber(s) to <strong>" +
                              this.selectedCompanyName + "</strong>. Each subscriber's SIMs move with them.";
          break;
        default:
          this.confirmSummary="";
          break;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private executeForRow(row: BulkUserRow): Observable<any> {
    try {
      switch(this.action) {
        case BulkUserAction.TransferCompany:
          return this.usersService.transferUser(<number>this.selectedCompanyId, row.userId);
        default:
          throw new Error("BulkUserActionsModalFormComponent.executeForRow() - Unsupported action");
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      throw e;
    }
  }

  private normaliseRows(rawRows: any[]): BulkUserRow[] {
    try {
      return (rawRows ?? []).map(row => ({
        companyId  : <number>(row?.companyID ?? row?.companyId ?? -1),
        companyName: <string>(row?.companyName ?? ""),
        userId     : <number>(row?.id ?? row?.userId ?? -1),
        userName   : <string>(row?.fullname ?? row?.fullName ?? row?.username ?? "")
      }));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  private loadCompanies(): void {
    try {
      this.companiesService.getIdAndDescriptionForLoggedInUser(this.curUser.userId)
      .subscribe({
        next : (data: IdAndDescriptionDTO[]) => { this.companies=data ?? []; },
        error: error                         => { this.exceptionsService.handleException(new Error(error)); }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private openModal(): void {
    try {
      this.modalRef=this.modalService.open(this.bulkUserActionsModalForm, { backdrop: this.modalBackGround,
                                                                           centered: true,
                                                                           keyboard: false,
                                                                           size    : msSize.ExtraLarge });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private processNext(): void {
    try {
      if (this.currentIndex>=this.totalCount) {
        this.processing=false;
        this.step      ="complete";

        this.auditBulk("Bulk " + this.actionLabel + " completed — " + this.successCount + " succeeded, " + this.errorCount + " failed of " + this.totalCount + " subscriber(s)",
                       this.auditResultsInfo());

        return;
      }

      const row: BulkUserRow=this.runRows[this.currentIndex];
      let   succeeded: boolean=false;
      let   message  : string="";

      this.processingSub=this.executeForRow(row)
      .subscribe({
        next    : (data: any) => {
          succeeded=data?.result===true;
          message  =succeeded ? "" : "The subscriber could not be transferred";
        },
        error   : error       => { this.recordResult(row, false, this.exceptionsService.formatHttpError(error)); this.advance(); },
        complete: ()          => { this.recordResult(row, succeeded, message); this.advance(); }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private advance(): void {
    try {
      this.currentIndex++;
      this.progressPercent=this.totalCount>0 ? Math.round((this.currentIndex / this.totalCount) * 100) : 100;
      this.processNext();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private recordResult(row: BulkUserRow, ok: boolean, message: string): void {
    try {
      this.results.push({
        companyName: row?.companyName ?? "",
        message    : message ?? "",
        ok         : ok,
        userId     : row?.userId      ?? -1,
        userName   : row?.userName    ?? ""
      });

      if (ok) {
        this.successCount++;
      } else {
        this.errorCount++;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private resetState(): void {
    try {
      this.confirmSummary   ="";
      this.currentIndex     =0;
      this.errorCount       =0;
      this.formSubmitted    =false;
      this.processing       =false;
      this.progressPercent  =0;
      this.results          =[];
      this.runRows          =[];
      this.selectedCompanyId=null;
      this.skippedRows      =[];
      this.successCount     =0;
      this.totalCount       =0;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion

  //#region Public Methods

  public cancelForm(): void {
    try {
      this.modalRef?.close();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public canProceedConfigure(): boolean {
    try {
      return this.selectedCompanyId != null && <number>this.selectedCompanyId>0;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public confirm(): void {
    try {
      if (this.totalCount<=0) {
        return;
      }

      this.currentIndex   =0;
      this.errorCount     =0;
      this.progressPercent=0;
      this.results        =[];
      this.successCount   =0;
      this.processing     =true;
      this.step           ="progress";

      this.auditBulk("Bulk " + this.actionLabel + " started — " + this.totalCount + " subscriber(s) to " + this.selectedCompanyName,
                     "UserIDs: " + this.userListOrNone(this.runRows.map(x => x.userId)));

      this.processNext();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public finishAndClose(): void {
    try {
      this.completed.emit();
      this.modalRef?.close();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public next(): void {
    try {
      this.formSubmitted=true;

      if (!this.canProceedConfigure()) {
        return;
      }

      this.buildRunRows();

      if (this.totalCount<=0) {
        return;
      }

      this.buildConfirmSummary();
      this.step="confirm";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public openForm(action: BulkUserAction, rawRows: any[]): void {
    try {
      if (rawRows==undefined || rawRows.length<=0) {
        throw new Error("BulkUserActionsModalFormComponent.openForm() - No subscribers were selected");
      }

      this.resetState();

      this.action      =action;
      this.selectedRows=this.normaliseRows(rawRows);
      this.step        ="configure";

      this.loadCompanies();
      this.openModal();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion
}
